import React from 'react';
import styled, { css } from 'styled-components';
import { GetColor } from 'venn-ui-kit';
import { dimensions, useAppPrintMode } from 'venn-components';
import { PageType } from 'venn-utils';
import GridPage from './pages/GridPage';
import DisclosurePage from './pages/DisclosurePage';
import { RenderedPage, studioPrintOrientationType } from 'venn-state';
import { useRecoilValue } from 'recoil';
import TitlePage from './pages/TitlePage';
import SectionPage from './pages/SectionPage';

export const PAGE_WRAPPER_CLASS = 'pageWrapper';
export const PAGE_CLASS = 'page';

export interface PageProps {
  zoom: number;
  /** Position in the rendered list — used for display and as a stable React key. */
  renderedIndex: number;
  totalPages: number;
  descriptor: RenderedPage;
}

const isContinuationPage = RenderedPage.$is('ContinuationPage');

const renderInner = ({
  descriptor,
  renderedIndex,
  totalPages,
}: {
  descriptor: RenderedPage;
  renderedIndex: number;
  totalPages: number;
}): React.ReactNode => {
  if (isContinuationPage(descriptor)) {
    return <GridPage descriptor={descriptor} renderedIndex={renderedIndex} totalPages={totalPages} />;
  }

  const { page } = descriptor;
  switch (page.type) {
    case PageType.DISCLOSURE:
      return <DisclosurePage />;
    case PageType.TITLE:
      return <TitlePage pageNumber={renderedIndex} page={page} />;
    case PageType.SECTION:
      return <SectionPage pageNumber={renderedIndex} page={page} />;
    case PageType.GRID:
    case PageType.CONTENTS:
    default:
      return <GridPage descriptor={descriptor} renderedIndex={renderedIndex} totalPages={totalPages} />;
  }
};

const PageComponent = ({ zoom, renderedIndex, descriptor, totalPages }: PageProps) => {
  const { inPrintMode } = useAppPrintMode();
  const printOrientationType = useRecoilValue(studioPrintOrientationType);
  const { totalWidth, totalHeight } = dimensions[printOrientationType];

  const width = totalWidth;
  const innerComponent = renderInner({ descriptor, renderedIndex, totalPages });
  const isDisclosurePage = !isContinuationPage(descriptor) && descriptor.page.type === PageType.DISCLOSURE;
  const height = isDisclosurePage && inPrintMode ? undefined : totalHeight;

  return (
    <OverflowWrapper
      width={width}
      height={height}
      id={`page-${renderedIndex}`}
      zoom={zoom}
      className={PAGE_WRAPPER_CLASS}
      inPrintMode={inPrintMode}
      pageIndex={renderedIndex}
    >
      <PageWrapper width={width} height={height} zoom={zoom} className={PAGE_CLASS} inPrintMode={inPrintMode}>
        {innerComponent}
      </PageWrapper>
    </OverflowWrapper>
  );
};

export const descriptorsEqual = (a: RenderedPage, b: RenderedPage): boolean => {
  if (isContinuationPage(a) && isContinuationPage(b)) {
    return (
      a.sourceBlockId === b.sourceBlockId &&
      a.sourcePageIndex === b.sourcePageIndex &&
      a.continuationIndex === b.continuationIndex &&
      a.totalContinuations === b.totalContinuations &&
      a.sourcePageVisibleHeight === b.sourcePageVisibleHeight &&
      a.sliceRowCount === b.sliceRowCount &&
      a.scrollOffsetPx === b.scrollOffsetPx &&
      a.sliceHeightPx === b.sliceHeightPx &&
      a.sliceStartRowIndex === b.sliceStartRowIndex &&
      a.sliceDataHeightPx === b.sliceDataHeightPx &&
      a.blockLayout.x === b.blockLayout.x &&
      a.blockLayout.y === b.blockLayout.y &&
      a.blockLayout.w === b.blockLayout.w &&
      a.blockLayout.h === b.blockLayout.h &&
      a.userBlocks === b.userBlocks
    );
  }
  if (!isContinuationPage(a) && !isContinuationPage(b)) {
    return a.sourceIndex === b.sourceIndex && a.page === b.page;
  }
  return false;
};

export const arePropsEqual = (prev: PageProps, next: PageProps): boolean =>
  prev.zoom === next.zoom &&
  prev.renderedIndex === next.renderedIndex &&
  prev.totalPages === next.totalPages &&
  descriptorsEqual(prev.descriptor, next.descriptor);

export default React.memo(PageComponent, arePropsEqual);

const OverflowWrapper = styled.div<{
  zoom: number;
  width: number;
  height?: number;
  inPrintMode: boolean;
  pageIndex: number;
}>`
  width: ${({ zoom, width }) => zoom * width}px;
  contain: paint layout style inline-size;

  ${({ zoom, height }) =>
    height &&
    css`
      height: ${zoom * height}px;
      /* Setting contain strict improves correctness for pages with defined height, preventing wide blocks 
          from harming page layout. */
      contain: strict;
    `};

  ${({ inPrintMode }) =>
    !inPrintMode &&
    css`
      box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
    `}

  ${({ pageIndex }) =>
    pageIndex > 0 &&
    css`
      page-break-before: always;
    `}
`;

const PageWrapper = styled.div<{ zoom: number; width: number; height?: number; inPrintMode: boolean }>`
  /* 
  * Overflow hidden is incredibly important to allow ag-grid and highcharts to properly render within the bounds of the page during printing.
  * See VENN-21027 for more information.
  */
  overflow: hidden;
  transform: scale(${({ zoom }) => zoom});
  transform-origin: top left;
  background-color: ${GetColor.White};
  display: flex;
  flex-direction: column;
  align-items: center;
  // TODO: remove empty functions
  width: ${({ width }) => width}px;
  ${({ height }) =>
    height &&
    css`
      height: ${height}px;
    `}

  ${({ inPrintMode }) =>
    !inPrintMode &&
    css`
      border: 1px solid ${GetColor.GreyScale.Grey10};
    `}

  // TODO: Remove when footer is no longer used in old reporting
  > :first-child {
    width: 100%;
  }
`;
